import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import {
  ArrowLeft, Shield, Loader2, Printer, Share2, Copy, Check, AlertCircle,
  Car, Gauge, Cog, Fuel, Ruler, Users, Factory, Globe, Plus, ChevronRight,
  Zap, Wind, Activity, Hash, Calendar, Wrench, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from '../LanguageContext';

const fmt = (v, suffix = '') => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return `${v}${suffix}`;
};

const fmtNum = (v, suffix = '') => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `${n.toLocaleString()}${suffix}`;
};

const Stat = ({ label, value, accent = false, testId }) => (
  <div className={`flex items-start justify-between py-2.5 border-b border-white/5 last:border-0 ${accent ? 'bg-cyan-500/[0.03] -mx-3 px-3' : ''}`}>
    <span className="text-[11px] text-gray-500 uppercase tracking-wider font-medium pr-3">{label}</span>
    <span className={`text-sm font-medium text-right ${value === '—' ? 'text-gray-600' : 'text-gray-100'}`} data-testid={testId}>{value}</span>
  </div>
);

const SectionCard = ({ icon: Icon, title, description, children, accentColor = 'cyan' }) => {
  const colorMap = {
    cyan: 'from-cyan-500/10 to-transparent border-cyan-500/20 text-cyan-400',
    purple: 'from-purple-500/10 to-transparent border-purple-500/20 text-purple-400',
    amber: 'from-amber-500/10 to-transparent border-amber-500/20 text-amber-400',
    emerald: 'from-emerald-500/10 to-transparent border-emerald-500/20 text-emerald-400',
    rose: 'from-rose-500/10 to-transparent border-rose-500/20 text-rose-400',
    sky: 'from-sky-500/10 to-transparent border-sky-500/20 text-sky-400',
  };
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/5 overflow-hidden">
      <div className={`px-4 py-3 bg-gradient-to-r ${colorMap[accentColor]} border-b flex items-center gap-2.5`}>
        <Icon className="w-4 h-4" />
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm font-bold tracking-wide">{title}</h3>
          {description && <p className="text-[10px] text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="px-4 py-2">
        {children}
      </div>
    </div>
  );
};

const KeyMetric = ({ label, value, unit, icon: Icon, color = 'cyan' }) => {
  const colorMap = {
    cyan: 'from-cyan-500/15 to-cyan-500/0 text-cyan-300 border-cyan-500/20',
    purple: 'from-purple-500/15 to-purple-500/0 text-purple-300 border-purple-500/20',
    amber: 'from-amber-500/15 to-amber-500/0 text-amber-300 border-amber-500/20',
    emerald: 'from-emerald-500/15 to-emerald-500/0 text-emerald-300 border-emerald-500/20',
  };
  return (
    <div className={`rounded-2xl p-4 bg-gradient-to-br ${colorMap[color]} border`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 opacity-80" />
        <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-heading font-bold text-white tracking-tight">
        {value === null || value === undefined || value === '' ? '—' : fmtNum(value)}
        {unit && <span className="text-sm font-medium ml-1 opacity-60">{unit}</span>}
      </div>
    </div>
  );
};

const VINReportPage = () => {
  const { t } = useTranslation();
  const { vin } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/vin-report/${vin}`);
        setReport(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || t('vin.notFound'));
      } finally {
        setLoading(false);
      }
    };
    if (vin) load();
  }, [vin, t]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success(t('common.linkCopied'));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/vin-report/${vin}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `carhipo-vin-report-${vin}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(t('vin.downloadPdf'));
    } catch {
      toast.error(t('common.error'));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="vin-report-loading">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mx-auto mb-3" />
          <p className="text-sm text-gray-400">{t('vin.loadingReport')} <span className="font-mono text-gray-300">{vin}</span>…</p>
          <p className="text-[11px] text-gray-600 mt-1">{t('vin.pullingDb')}</p>
        </div>
      </div>
    );
  }

  if (error || !report || report.valid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="vin-report-error">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="font-heading text-xl font-bold mb-2">{t('vin.notFound')}</h1>
          <p className="text-sm text-gray-400 mb-1">{error || report?.reason || t('vinPage.cantDecode')}</p>
          <p className="text-[11px] text-gray-600 font-mono mb-5">{vin}</p>
          <Link to="/vin-check">
            <Button variant="outline" className="border-white/10" data-testid="vin-report-try-another">{t('vin.tryAnother')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { identity, powertrain, performance, dimensions, capacity, manufacturer } = report;
  const horsepower = powertrain.engine_power_hp;
  const topSpeed = performance.max_speed_kmh;
  const co2 = performance.co2_emissions_g_km;
  const weight = dimensions.weight_empty_kg;

  return (
    <div className="min-h-screen pb-16" data-testid="vin-report-page">
      {/* HERO */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-purple-500/5 pointer-events-none" />
        <div className="absolute -top-32 -right-24 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-cyan-400 mb-5 transition-colors" data-testid="vin-report-back">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('common.back')}
          </button>

          <div className="flex items-start justify-between flex-wrap gap-5">
            <div className="flex items-center gap-4 sm:gap-5 min-w-0">
              {report.make_logo ? (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white p-3 shrink-0">
                  <img src={report.make_logo} alt={identity.brand} className="w-full h-full object-contain" data-testid="vin-report-make-logo" />
                </div>
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
                  <Car className="w-7 h-7 sm:w-9 sm:h-9 text-cyan-400" />
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
                    <Shield className="w-3 h-3" /> {t('vin.verified')}
                  </span>
                  <span className="text-[11px] text-gray-500">·</span>
                  <span className="text-[11px] text-gray-500">{t('vin.vincarioDb')}</span>
                </div>
                <h1 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight" data-testid="vin-report-title">{report.title}</h1>
                <p className="font-mono text-[11px] sm:text-xs text-gray-500 mt-2" data-testid="vin-report-vin">VIN · {report.vin}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={handleDownloadPdf} disabled={downloading} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold h-9 text-xs" data-testid="vin-report-pdf">
                {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                {downloading ? t('vin.preparing') : t('vin.downloadPdf')}
              </Button>
              <Button onClick={handleCopy} variant="outline" size="sm" className="border-white/10 h-9 text-xs" data-testid="vin-report-copy">
                {copied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                {copied ? t('vin.copied') : t('vin.copyLink')}
              </Button>
              <Button onClick={handlePrint} variant="outline" size="sm" className="border-white/10 h-9 text-xs" data-testid="vin-report-print">
                <Printer className="w-3.5 h-3.5 mr-1.5" /> {t('vin.print')}
              </Button>
            </div>
          </div>

          {/* KEY METRICS */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            <KeyMetric label={t('vin.power')} value={horsepower} unit="HP" icon={Zap} color="amber" />
            <KeyMetric label={t('vin.topSpeed')} value={topSpeed} unit="km/h" icon={Gauge} color="cyan" />
            <KeyMetric label={t('vin.co2')} value={co2} unit="g/km" icon={Wind} color="emerald" />
            <KeyMetric label={t('vin.weight')} value={weight} unit="kg" icon={Activity} color="purple" />
          </div>

          {/* CTA STRIP */}
          <div className="mt-6 rounded-xl p-4 bg-white/[0.03] border border-white/5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-cyan-500/15 flex items-center justify-center shrink-0">
                <Plus className="w-4 h-4 text-cyan-400" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">{t('vin.sellingCar')}</div>
                <div className="text-[11px] text-gray-500">{t('vin.sellingCarDesc')}</div>
              </div>
            </div>
            <Link to={`/dashboard/add-car?vin=${vin}`}>
              <Button className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold h-9 text-xs" data-testid="vin-report-list-cta">
                {t('vin.listThisCar')} <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* SECTIONS */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard icon={Hash} title={t('vin.identity')} description={t('vin.identityDesc')} accentColor="cyan">
            <Stat label={t('vin.make')} value={fmt(identity.brand)} testId="vin-stat-brand" />
            <Stat label={t('vin.modelLbl')} value={fmt(identity.model)} testId="vin-stat-model" />
            <Stat label={t('vin.modelYear')} value={fmt(identity.model_year)} testId="vin-stat-year" />
            <Stat label={t('vin.trim')} value={fmt(identity.trim)} />
            <Stat label={t('vin.series')} value={fmt(identity.series)} />
            <Stat label={t('vin.bodyStyle')} value={fmt(identity.body)} />
            <Stat label={t('vin.productType')} value={fmt(identity.product_type)} />
          </SectionCard>

          <SectionCard icon={Cog} title={t('vin.powertrain')} description={t('vin.powertrainDesc')} accentColor="amber">
            <Stat label={t('vin.fuelType')} value={fmt(powertrain.fuel_type)} />
            <Stat label={t('vin.displacement')} value={fmtNum(powertrain.engine_displacement_cc, ' cc')} />
            <Stat label={t('vin.power')} value={fmtNum(powertrain.engine_power_hp, ' HP')} accent />
            <Stat label={t('vin.powerKw')} value={fmtNum(powertrain.engine_power_kw, ' kW')} />
            <Stat label={t('vin.cylinders')} value={fmt(powertrain.cylinders)} />
            <Stat label={t('vin.transmission')} value={fmt(powertrain.transmission)} />
            <Stat label={t('vin.drivetrain')} value={fmt(powertrain.drive_type)} />
          </SectionCard>

          <SectionCard icon={Activity} title={t('vin.performance')} description={t('vin.performanceDesc')} accentColor="emerald">
            <Stat label={t('vin.topSpeed')} value={fmtNum(performance.max_speed_kmh, ' km/h')} accent />
            <Stat label={t('vin.co2Em')} value={fmtNum(performance.co2_emissions_g_km, ' g/km')} />
            <Stat label={t('vin.fuelComb')} value={fmtNum(performance.fuel_consumption_combined, ' l/100km')} />
            <Stat label={t('vin.fuelUrban')} value={fmtNum(performance.fuel_consumption_urban, ' l/100km')} />
            <Stat label={t('vin.fuelExtra')} value={fmtNum(performance.fuel_consumption_extra, ' l/100km')} />
          </SectionCard>

          <SectionCard icon={Ruler} title={t('vin.dimensions')} description={t('vin.dimensionsDesc')} accentColor="purple">
            <Stat label={t('vin.length')} value={fmtNum(dimensions.length_mm, ' mm')} />
            <Stat label={t('vin.width')} value={fmtNum(dimensions.width_mm, ' mm')} />
            <Stat label={t('vin.height')} value={fmtNum(dimensions.height_mm, ' mm')} />
            <Stat label={t('vin.wheelbase')} value={fmtNum(dimensions.wheelbase_mm, ' mm')} />
            <Stat label={t('vin.curbWeight')} value={fmtNum(dimensions.weight_empty_kg, ' kg')} />
            <Stat label={t('vin.maxWeight')} value={fmtNum(dimensions.max_weight_kg, ' kg')} />
            <Stat label={t('vin.maxRoofLoad')} value={fmtNum(dimensions.max_roof_load_kg, ' kg')} />
          </SectionCard>

          <SectionCard icon={Users} title={t('vin.capacity')} description={t('vin.capacityDesc')} accentColor="sky">
            <Stat label={t('vin.doors')} value={fmt(capacity.doors)} />
            <Stat label={t('vin.seats')} value={fmt(capacity.seats)} />
            <Stat label={t('vin.axles')} value={fmt(capacity.axles)} />
            <Stat label={t('vin.abs')} value={fmt(report.safety?.abs)} />
          </SectionCard>

          <SectionCard icon={Factory} title={t('vin.manufacturer')} description={t('vin.manufacturerDesc')} accentColor="rose">
            <Stat label={t('vin.manufacturer')} value={fmt(manufacturer.name)} />
            <Stat label={t('vin.address')} value={fmt(manufacturer.address)} />
            <Stat label={t('vin.plantCountry')} value={fmt(manufacturer.plant_country)} />
            <Stat label={t('vin.vehicleId')} value={fmt(identity.vehicle_id)} />
            <Stat label={t('vin.checkDigit')} value={fmt(identity.check_digit)} />
            <Stat label={t('vin.seqNumber')} value={fmt(identity.sequential_number)} />
          </SectionCard>
        </div>

        {/* RAW LABELS — collapsible */}
        {report.raw && Object.keys(report.raw).length > 0 && (
          <details className="mt-6 rounded-2xl bg-white/[0.02] border border-white/5 overflow-hidden">
            <summary className="cursor-pointer px-4 py-3 text-xs text-gray-400 hover:text-gray-200 transition-colors flex items-center gap-2">
              <Wrench className="w-3.5 h-3.5" />
              {t('vin.viewRaw').replace('{n}', Object.keys(report.raw).length)}
            </summary>
            <div className="px-4 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                {Object.entries(report.raw).map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between py-1.5 border-b border-white/5 text-[11px]">
                    <span className="text-gray-500 pr-3">{k}</span>
                    <span className="font-mono text-gray-300 text-right break-all">{Array.isArray(v) ? v.join(', ') : (v === null || v === '' ? '—' : String(v))}</span>
                  </div>
                ))}
              </div>
            </div>
          </details>
        )}

        {/* FOOTER */}
        <div className="mt-8 flex items-center justify-center gap-2 text-[11px] text-gray-600">
          <Globe className="w-3 h-3" />
          {t('vin.powered')} · {t('vin.reportGenerated')} {new Date(report.generated_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default VINReportPage;
